import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import LanIcon from '@mui/icons-material/Lan';
import TimelineIcon from '@mui/icons-material/Timeline';
import GppBadIcon from '@mui/icons-material/GppBad';
import CloseIcon from '@mui/icons-material/Close';
import CustomizedSnackbars from '../../components/material-snackbars';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import { UsageEllipsisCell, UsageKpiCard, UsageApiChip, UsageEmpty, UsageBlockTitle, UsageKindChip, UsageStatusChip, UsageOriginChip, STICKY_CELL_CLASS, SELECTED_ROW_CLASS, fmtDateTime, fmtInt, fmtMs } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Aba "Tráfego por IP": responde "qual IP fez N mil requests para o mesmo endpoint, e quando?".
// Lê o resumo por IP (tb_user_usage_ip_daily: dia/hora/IP/API/rota), recalculado pelo mesmo job
// do dashboard. Ranking de IPs → clique num IP: endpoints com o pico por hora + série. O limiar
// marca como "suspeito" quem passou dele numa mesma hora para um mesmo endpoint.
// O IP da área (filtro comum) restringe o ranking a esse IP e já abre o painel dele.
// Clique num endpoint: além da série só dele, abre à direita (empurrando a página, sem sobrepor) a lista das
// requisições daquele IP naquele endpoint — data/hora, usuário, status, duração, tela — e cada linha abre o
// drawer de detalhe comum às outras abas. Fonte: linha do tempo (fato) filtrada por ip + api + rota.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const REQ_PAGE_SIZE = 20;
const DEFAULT_THRESHOLD = 1000;

export default class UsageIpTraffic extends Component {
  constructor(props) {
    super(props);
    const q = new URLSearchParams(props.location?.search || '');
    const start = q.get('start'); const end = q.get('end');
    const range = start && end
      ? { preset: 'custom', dateStart: moment(start).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(end).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
      : { preset: '7d', ...presetRange('7d') };
    this.state = {
      filter: { ...range, api: '', actionGroup: '', userId: 0, userName: '', ip: props.ip || '' },
      threshold: DEFAULT_THRESHOLD, minRequests: 0,
      loading: false, loaded: false, rows: [], count: 0, page: 1, totals: { ips: 0, requests: 0, suspicious: 0 },
      sort: { key: 'requests', dir: 'desc' },
      selected: '', endpoints: [], series: [], granularity: 'hour', detailLoading: false, selectedRoute: '', selectedApi: '',
      requests: { open: false, loading: false, rows: [], count: 0, page: 1 }, drawerRow: null,
      snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.onPage = this.onPage.bind(this);
    this.select = this.select.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
    this.onRequestsPage = this.onRequestsPage.bind(this);
    this.closeRequests = this.closeRequests.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(1); }

  // IP vem da área (index.js): mudou em outra aba → aplica aqui e recarrega o que já estava carregado.
  componentDidUpdate(prevProps) {
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }), () => { if (this.state.loaded) this.load(1); });
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) {
    if ((next.ip || '') !== (this.state.filter.ip || '') && this.props.onIpChange) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }
  onPage(e, page) { this.load(page); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }
  sortBy(key) { this.setState((s) => ({ sort: { key, dir: s.sort.key === key && s.sort.dir === 'desc' ? 'asc' : 'desc' } })); }
  days() { const f = this.state.filter; return moment(f.dateEnd).diff(moment(f.dateStart), 'days') + 1; }

  load(page) {
    const f = this.state.filter;
    const threshold = Math.max(1, Number(this.state.threshold) || DEFAULT_THRESHOLD);
    this.setState({ loading: true, page, selected: '', endpoints: [], series: [], selectedRoute: '', selectedApi: '', requests: { open: false, loading: false, rows: [], count: 0, page: 1 } });
    UsageService.ipRanking(f, threshold, Number(this.state.minRequests) || 0, page, PAGE_SIZE)
      .then((r) => {
        this.setState({ loading: false, loaded: true, rows: r.data, count: r.count, totals: r.totals }, () => {
          // Ranking restrito a um IP (filtro da área): abre o painel dele direto.
          if (f.ip && r.data.length === 1) this.select(r.data[0].ip, true);
        });
      })
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  // Abre o painel de um IP: endpoints (com pico por hora) + série. Clicar de novo fecha.
  select(ip, force) {
    const noRequests = { open: false, loading: false, rows: [], count: 0, page: 1 };
    if (!force && this.state.selected === ip) { this.setState({ selected: '', endpoints: [], series: [], selectedRoute: '', selectedApi: '', requests: noRequests }); return; }
    const f = this.state.filter;
    const granularity = this.days() > 14 ? 'day' : 'hour';
    this.setState({ selected: ip, endpoints: [], series: [], selectedRoute: '', selectedApi: '', requests: noRequests, granularity, detailLoading: true });
    Promise.all([UsageService.ipEndpoints(ip, f), UsageService.ipSeries(ip, f, '', granularity)])
      .then(([endpoints, series]) => this.setState({ endpoints, series, detailLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ detailLoading: false }); this.notify('error', err.message); });
  }

  // Série de UMA rota do IP (clique na linha de endpoint) + painel com as requisições dela; clicar de novo
  // volta ao IP inteiro e fecha o painel.
  selectRoute(route, api) {
    const same = this.state.selectedRoute === route && this.state.selectedApi === (api || '');
    const next = same ? '' : route;
    this.setState({ selectedRoute: next, selectedApi: same ? '' : (api || ''), detailLoading: true, requests: { open: !same, loading: !same, rows: [], count: 0, page: 1 } });
    UsageService.ipSeries(this.state.selected, this.state.filter, next, this.state.granularity)
      .then((series) => this.setState({ series, detailLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ detailLoading: false }); this.notify('error', err.message); });
    if (!same) this.loadRequests(1, route, api || '');
  }

  // Requisições do IP no endpoint: a linha do tempo do fato filtrada por ip + api + rota. Rota vazia no resumo
  // (request sem rota reconhecida) não recorta por rota — lista tudo do IP naquela API.
  loadRequests(page, route, api) {
    const f = this.state.filter;
    this.setState((s) => ({ requests: { ...s.requests, open: true, loading: true, page } }));
    UsageService.activity({ userId: 0, dateStart: f.dateStart, dateEnd: f.dateEnd, page, pageSize: REQ_PAGE_SIZE, ip: this.state.selected, api: api || undefined, route: route || undefined })
      .then((r) => this.setState({ requests: { open: true, loading: false, rows: r.data, count: r.count, page } }))
      .catch((err) => { Api.kickoff(err); this.setState((s) => ({ requests: { ...s.requests, loading: false } })); this.notify('error', err.message); });
  }
  onRequestsPage(e, page) { this.loadRequests(page, this.state.selectedRoute, this.state.selectedApi); }
  closeRequests() { this.setState({ requests: { open: false, loading: false, rows: [], count: 0, page: 1 } }); }
  closeDrawer() { this.setState({ drawerRow: null }); }

  setGranularity(g) {
    if (!g || g === this.state.granularity) return;
    this.setState({ granularity: g, detailLoading: true });
    UsageService.ipSeries(this.state.selected, this.state.filter, this.state.selectedRoute, g)
      .then((series) => this.setState({ series, detailLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ detailLoading: false }); this.notify('error', err.message); });
  }

  // Leva o IP para as outras abas (o filtro da área carrega o IP junto).
  goTab(tab, ip) {
    const f = this.state.filter;
    if (this.props.onIpChange) this.props.onIpChange(ip);
    this.props.history.push(`/${this.props.lang}/usage/${tab}?start=${moment(f.dateStart).format('YYYY-MM-DD')}&end=${moment(f.dateEnd).format('YYYY-MM-DD')}&ip=${encodeURIComponent(ip)}`);
  }

  renderRanking() {
    const { intl } = this.props;
    const { rows, sort, selected, threshold } = this.state;
    if (!rows.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.ipt.none', 'Nenhum IP no período com esses filtros')} />;
    const cols = [
      ['ip', 'IP', 'left'], ['requests', this.tr('usage.ipt.col.requests', 'Requests'), 'right'], ['endpoints', this.tr('usage.ipt.col.endpoints', 'Endpoints'), 'right'],
      ['users', this.tr('usage.col.users', 'Usuários'), 'right'], ['denials', this.tr('usage.col.denials', 'Recusas'), 'right'], ['errors', this.tr('usage.col.errors', 'Erros 5xx'), 'right'],
      ['peakHourRequests', this.tr('usage.ipt.col.peak', 'Pico (hora × endpoint)'), 'right'], ['firstAt', this.tr('usage.col.first', 'Primeiro'), 'left'], ['lastAt', this.tr('usage.col.last', 'Último'), 'left'],
    ];
    const sorted = [...rows].sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      const r = typeof va === 'string' ? String(va).localeCompare(String(vb)) : (Number(va) || 0) - (Number(vb) || 0);
      return sort.dir === 'asc' ? r : -r;
    });
    const limit = Math.max(1, Number(threshold) || DEFAULT_THRESHOLD);
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            {cols.map(([k, l, al]) => <TableCell key={k} align={al}><TableSortLabel active={sort.key === k} direction={sort.key === k ? sort.dir : 'desc'} onClick={() => this.sortBy(k)}>{l}</TableSortLabel></TableCell>)}
            <TableCell className={STICKY_CELL_CLASS} />
          </TableRow></TableHead>
          <TableBody>
            {sorted.map((r) => {
              const suspicious = r.peakHourRequests >= limit;
              return (
                <TableRow key={r.ip} hover selected={selected === r.ip} className={selected === r.ip ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer', background: suspicious && selected !== r.ip ? '#fff8e1' : undefined }} onClick={() => this.select(r.ip)}>
                  <TableCell style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontWeight: selected === r.ip ? 700 : 500 }}>
                    {r.ip}{suspicious && <Chip size="small" label={this.tr('usage.ipt.suspicious', 'suspeito')} style={{ marginLeft: 8, height: 20, fontSize: 11, backgroundColor: '#F44336', color: '#fff', fontWeight: 600 }} />}
                  </TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}><b>{fmtInt(r.requests)}</b></TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.endpoints)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.users === null ? <Tooltip title={this.tr('usage.dash.users.ipsTooLongTip', 'Os IPs vêm do histórico detalhado, lido só em períodos de até 90 dias')}><span>—</span></Tooltip> : fmtInt(r.users)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.denials ? '#c62828' : undefined }}>{fmtInt(r.denials)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.errors ? '#c62828' : undefined }}>{fmtInt(r.errors)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: suspicious ? '#c62828' : undefined, fontWeight: suspicious ? 700 : undefined }}>
                    <Tooltip title={r.peakRoute ? `${r.peakApi} · ${r.peakRoute} · ${fmtDateTime(r.peakAt)}` : ''}><span>{fmtInt(r.peakHourRequests)}</span></Tooltip>
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.firstAt)}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.lastAt)}</TableCell>
                  <TableCell className={STICKY_CELL_CLASS}>
                    <Button size="small" startIcon={<TimelineIcon />} onClick={(e) => { e.stopPropagation(); this.goTab('user-activity', r.ip); }}>{this.tr('usage.ipt.timeline', 'linha do tempo')}</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  renderDetail() {
    const { intl } = this.props;
    const { selected, endpoints, series, granularity, detailLoading, selectedRoute, selectedApi, threshold } = this.state;
    if (!selected) return null;
    const limit = Math.max(1, Number(threshold) || DEFAULT_THRESHOLD);
    const data = series.map((s) => ({ label: granularity === 'hour' ? moment(s.at).format('DD/MM HH:mm') : moment(s.at).format('DD/MM'), requests: s.requests, denials: s.denials, errors: s.errors }));
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #3F51B4', marginTop: 12 }}><CardContent>
        <UsageBlockTitle title={<span>IP <span style={{ fontFamily: 'monospace' }}>{selected}</span></span>}
          subtitle={selectedRoute ? `${this.tr('usage.ipt.detail.route', 'Série da rota')}: ${selectedRoute}` : this.tr('usage.ipt.detail.subtitle', 'Endpoints chamados e a série no período · clique numa rota para ver só ela e as requisições dela')}
          right={
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <ToggleButtonGroup size="small" exclusive value={granularity} onChange={(e, v) => this.setGranularity(v)}>
                <ToggleButton value="hour">{this.tr('usage.ipt.byHour', 'Por hora')}</ToggleButton><ToggleButton value="day">{this.tr('usage.ipt.byDay', 'Por dia')}</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTab('user-activity', selected)}>{this.tr('usage.ipt.timeline', 'linha do tempo')}</Button>
              <Button size="small" startIcon={<GppBadIcon />} onClick={() => this.goTab('blocked', selected)}>{this.tr('usage.ipt.denials', 'recusas')}</Button>
              <Button size="small" startIcon={<CloseIcon />} onClick={() => this.select(selected)}>{this.tr('usage.ipt.close', 'fechar')}</Button>
            </Box>} />
        {detailLoading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        {!detailLoading && (data.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis allowDecimals={false} /><ChartTooltip formatter={(v, n) => [fmtInt(v), n]} /><Legend />
              <Bar dataKey="requests" name={this.tr('usage.ipt.col.requests', 'Requests')} fill="#3F51B4" />
              <Bar dataKey="denials" name={this.tr('usage.col.denials', 'Recusas')} fill="#F44336" />
              <Bar dataKey="errors" name={this.tr('usage.col.errors', 'Erros 5xx')} fill="#B71C1C" />
            </BarChart>
          </ResponsiveContainer>
        ) : <UsageEmpty intl={intl} compact />)}
        <Box style={{ overflowX: 'auto', marginTop: 8 }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>API</TableCell><TableCell>{this.tr('usage.col.route', 'Rota')}</TableCell><TableCell align="right">{this.tr('usage.ipt.col.requests', 'Requests')}</TableCell>
              <TableCell align="right">{this.tr('usage.col.denials', 'Recusas')}</TableCell><TableCell align="right">{this.tr('usage.col.errors', 'Erros 5xx')}</TableCell>
              <TableCell align="right">{this.tr('usage.ipt.col.peakHour', 'Pico numa hora')}</TableCell><TableCell>{this.tr('usage.ipt.col.peakAt', 'Quando')}</TableCell>
              <TableCell>{this.tr('usage.col.first', 'Primeiro')}</TableCell><TableCell>{this.tr('usage.col.last', 'Último')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {endpoints.map((e) => {
                const hot = e.peakHourRequests >= limit;
                return (
                  <TableRow key={`${e.api}|${e.route}`} hover selected={selectedRoute === e.route && selectedApi === e.api} className={selectedRoute === e.route && selectedApi === e.api ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer', background: hot && selectedRoute !== e.route ? '#fff8e1' : undefined }} onClick={() => this.selectRoute(e.route, e.api)}>
                    <TableCell><UsageApiChip api={e.api} /></TableCell>
                    <TableCell><UsageEllipsisCell text={e.route || '-'} maxWidth={320} mono bold={hot} /></TableCell>
                    <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}><b>{fmtInt(e.requests)}</b></TableCell>
                    <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: e.denials ? '#c62828' : undefined }}>{fmtInt(e.denials)}</TableCell>
                    <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: e.errors ? '#c62828' : undefined }}>{fmtInt(e.errors)}</TableCell>
                    <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: hot ? '#c62828' : undefined, fontWeight: hot ? 700 : undefined }}>{fmtInt(e.peakHourRequests)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.peakAt)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.firstAt)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.lastAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent></Card>
    );
  }

  // Painel lateral: requisições do IP no endpoint selecionado. Linha → drawer de detalhe (URL, User-Agent,
  // request id, exceção, JSON de detalhes), o mesmo das outras abas.
  renderRequests() {
    const { intl } = this.props;
    const { selected, selectedRoute, selectedApi, requests } = this.state;
    const anon = this.tr('usage.anonymous', 'anônimo');
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #277afe' }}><CardContent style={{ paddingBottom: 8 }}>
        <UsageBlockTitle title={<span>{this.tr('usage.ipt.req.title', 'Requisições')} · <span style={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>{selectedRoute || this.tr('usage.ipt.req.noRoute', 'sem rota')}</span></span>}
          subtitle={<span><span style={{ fontFamily: 'monospace' }}>{selected}</span> · <UsageApiChip api={selectedApi} /> · {fmtInt(requests.count)} {this.tr('usage.ipt.req.subtitle', 'no período · clique na linha para ver o detalhe')}</span>}
          right={<Button size="small" startIcon={<CloseIcon />} onClick={this.closeRequests}>{this.tr('usage.ipt.close', 'fechar')}</Button>} />
        {requests.loading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        {!requests.loading && !requests.rows.length && <UsageEmpty intl={intl} compact title={this.tr('usage.ipt.req.none', 'Nenhuma requisição desse endpoint no período')} />}
        {!requests.loading && requests.rows.length > 0 && (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell><TableCell>{this.tr('usage.col.action', 'Ação')}</TableCell>
                <TableCell>Status</TableCell><TableCell align="right">{this.tr('usage.col.duration', 'Duração')}</TableCell><TableCell>{this.tr('usage.col.origin', 'Origem')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {requests.rows.map((r) => (
                  <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => this.setState({ drawerRow: r })}>
                    <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt, true)}</TableCell>
                    <TableCell><UsageEllipsisCell text={r.userName || (r.userId ? `#${r.userId}` : anon)} maxWidth={140} /></TableCell>
                    <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                    <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>{fmtMs(r.durationMs)}</TableCell>
                    <TableCell><UsageOriginChip intl={intl} row={r} maxWidth={140} />{r.details && r.details.forwarded ? <span style={{ color: '#c62828' }}> ⚠</span> : null}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {requests.count > REQ_PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination size="small" count={Math.ceil(requests.count / REQ_PAGE_SIZE)} page={requests.page} onChange={this.onRequestsPage} /></Box>}
      </CardContent></Card>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, threshold, minRequests, loading, loaded, count, page, totals, snack, requests, drawerRow } = this.state;
    const extra = (
      <>
        <Grid item xs={6} sm={3} md={2}>
          <Tooltip title={this.tr('usage.ipt.threshold.help', 'Requests numa MESMA hora para um MESMO endpoint a partir dos quais o IP é marcado como suspeito.')}>
            <TextField fullWidth size="small" type="number" label={this.tr('usage.ipt.threshold', 'Limiar/hora')} value={threshold} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ threshold: e.target.value })} />
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Tooltip title={this.tr('usage.ipt.minRequests.help', 'Esconde do ranking os IPs com menos requests que isto no período.')}>
            <TextField fullWidth size="small" type="number" label={this.tr('usage.ipt.minRequests', 'Mín. requests')} value={minRequests} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ minRequests: e.target.value })} />
          </Tooltip>
        </Grid>
      </>
    );
    return (
      <Box className="usage-ipt-layout">
      <Box className="usage-ipt-main">
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showGroup={false} showUser={false} extra={extra} onApply={() => this.load(1)} applying={loading} />
        <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.ipt.hint', 'Fonte: resumo por IP (dia/hora/API/rota), refeito de hora em hora pelo mesmo job do dashboard. Usuários distintos vêm do histórico detalhado, só em períodos de até 90 dias.')}</Typography>
        {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
        {!loading && loaded && (
          <Box style={{ marginTop: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ipt.kpi.ips', 'IPs distintos')} value={totals.ips} color="#3F51B4" /></Grid>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ipt.col.requests', 'Requests')} value={totals.requests} color="#607D8B" /></Grid>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ipt.kpi.suspicious', 'IPs acima do limiar')} value={totals.suspicious} color="#F44336" subtitle={`≥ ${fmtInt(Math.max(1, Number(threshold) || DEFAULT_THRESHOLD))} ${this.tr('usage.ipt.kpi.suspicious.sub', 'req/hora no mesmo endpoint')}`} /></Grid>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ipt.kpi.listed', 'IPs listados')} value={count} color="#009688" /></Grid>
            </Grid>
            {totals.ips === 0 ? <Box style={{ marginTop: 16 }}><UsageEmpty intl={intl} icon={<LanIcon style={{ fontSize: 56 }} />} title={this.tr('usage.ipt.empty.title', 'Nenhum tráfego por IP no período')} description={this.tr('usage.ipt.empty.description', 'O resumo por IP é refeito de hora em hora. Se o período é recente, use "Atualizar agora" no Dashboard.')} /></Box> : (
              <Box>
                {totals.suspicious > 0 && <Alert severity="warning" style={{ marginTop: 12 }}>{fmtInt(totals.suspicious)} {this.tr('usage.ipt.alert', 'IP(s) passaram do limiar numa mesma hora para um mesmo endpoint. Linhas destacadas; o pico mostra a rota e o horário ao passar o mouse.')}</Alert>}
                <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginTop: 12 }}><CardContent style={{ paddingBottom: 8 }}>
                  <UsageBlockTitle title={this.tr('usage.ipt.ranking.title', 'IPs por volume')} subtitle={this.tr('usage.ipt.ranking.subtitle', 'Clique num IP para ver os endpoints e a série')} />
                  {this.renderRanking()}
                  {count > PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} /></Box>}
                </CardContent></Card>
                {this.renderDetail()}
              </Box>
            )}
          </Box>
        )}
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={this.closeDrawer} lang={this.props.lang} history={this.props.history} onCopy={() => this.notify('success', this.tr('usage.copied', 'Copiado!'))} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
      {requests.open && <Box className="usage-ipt-side">{this.renderRequests()}</Box>}
      </Box>
    );
  }
}
